import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { DISPLAY_CURRENCIES } from "common/constants";
import { MINUS } from "features/bots/BotForm/Backtesting/NewLook/format";
import { compactAxisValue } from "features/bots/NewLook/newLookBotUtils";

/**
 * Display-currency amounts written the dashboard's way: "$10,000",
 * "−$454.90", "+$559.51" (₿ when the display currency is BTC), plus a
 * compact form for chart axes ("$1.2K").
 */
const useBacktestMoney = () => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const money = useCallback(
    (value: number) =>
      `${value < 0 ? MINUS : ""}${symbol}${getLocaleStringWithPrecision(
        Math.abs(value),
        precision
      )}`,
    [symbol, precision]
  );
  const signedMoney = useCallback(
    (value: number) => `${value > 0 ? "+" : ""}${money(value)}`,
    [money]
  );
  const compactMoney = useCallback(
    (value: number, step: number) =>
      compactAxisValue(value, step, { prefix: symbol }),
    [symbol]
  );

  return useMemo(
    () => ({ isBtc, symbol, precision, money, signedMoney, compactMoney }),
    [isBtc, symbol, precision, money, signedMoney, compactMoney]
  );
};

export default useBacktestMoney;
